import React from 'react';
import RecruiterPositionCardTitle from './RecruiterPositionCardTitle';
import RecruiterPositionCardDetail from './RecruiterPositionCardDetail';
import RecruiterPositionCardRequiredSkills from './RecruiterPositionCardRequiredSkills';
import RecruiterPositionCardFooter from './RecruiterPositionCardFooter';
import RecruiterPositionCardMoreOptions from './RecruiterPositionCardMoreOptions';
import { MyPositionType, PositionStatus, PositionTitle } from 'types';
import { useNavigate, useParams } from 'react-router-dom';
import { generatePositionTitle } from 'shared-utils';
import { showRecommendedConnectedUserCount } from 'src/utils/recruiter.util';
import { useUserContext } from 'src/context/UserProvider';
import { useToast } from 'src/context/ToastProvider';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { Badge } from 'flowbite-react';
import dayjs from 'dayjs';
import { CandidatesTabsEnum } from './RecruiterCandidatesContainer2';

interface Props {
	positionObj: MyPositionType;
	handleClosePosition: () => void;
	handleViewPosition: (id: string) => void;
	handleEditPosition?: (id: string) => void;
	handleReopenPosition: (id: string) => void;
	handleDuplicatePosition?: (id: string) => void;
	handleAddCollaborator: (id: string) => void;
	optionalData?: { title: string; data: number }[];
	handleUpdatePositionExpiry: (id: string) => void;
	handleUpdateApplicationExpiry?: (id: string) => void;
	handleUpdateAutoRecommendationExpiry?: (id: string) => void;
	handleViewRecommendationsTrigger?: (id: string) => void;
}

const RecruiterPositionCard = ({
	positionObj,
	handleClosePosition,
	handleViewPosition,
	handleEditPosition,
	handleDuplicatePosition,
	handleAddCollaborator,
	handleUpdatePositionExpiry,
	handleUpdateApplicationExpiry,
	handleUpdateAutoRecommendationExpiry,
	handleReopenPosition,
	handleViewRecommendationsTrigger,
	optionalData,
}: Props) => {
	const navigate = useNavigate();
	const { companyId } = useParams();
	const toast = useToast();
	const { user, isAccountManagerLoggedIn, isRecruiterLoggedIn } = useUserContext();
	const { setSelectedPositionId } = useRecommendationsContext();

	const positionId = String(positionObj.position._id);

	const handleViewRecommendations = () => {
		setSelectedPositionId(positionId);
		if (handleViewRecommendationsTrigger) {
			return handleViewRecommendationsTrigger(positionId);
		}
		if (!positionObj.position.company.isLead) {
			if (positionObj.position.hasConfirmedPublish) {
				if (isAccountManagerLoggedIn)
					navigate(`/positions/${companyId}/${positionId}/candidates?tab=${CandidatesTabsEnum.RECOMMENDATIONS}`);
				else
					navigate(
						`/positions/${positionId}/candidates?tab=${user?.flags?.hideRecommendations && isRecruiterLoggedIn
							? CandidatesTabsEnum.CONNECTED
							: CandidatesTabsEnum.RECOMMENDATIONS
						}`,
					);
			} else if (positionObj.position.positionStatus === PositionStatus.OPEN) {
				navigate(`/job-preview/${isAccountManagerLoggedIn ? `${companyId}/` : ''}${positionId}`);
			}
		} else {
			toast({
				title: 'This company does not have any recommendations because this is lead company',
				type: 'warning',
			});
		}
	};

	const canShowRecommendedConnectedUserCount =
		isAccountManagerLoggedIn ||
		showRecommendedConnectedUserCount(positionObj.recommendedUsers || 0, positionObj.connectedUsers || 0);

	return (
		<>
			<div className="universal-card-hover-effect rounded-[12px] dark:bg-[#1F2A37] bg-[#fff] border border-solid border-[#C7C9D9] dark:border-[#c7c9d91a] relative flex flex-col h-full hover:cursor-pointer shadow-up transition-shadow">
				<div className="absolute top-[-11px] right-[10px] flex gap-2 whitespace-pre">
					{isAccountManagerLoggedIn ? null : (
						<Badge>
							Created By {positionObj?.position?.createdByID?.toString() === user?._id?.toString() ? 'You' : 'Others'}
						</Badge>
					)}

					{isAccountManagerLoggedIn &&
						positionObj?.position?.positionStatus !== PositionStatus.CLOSED &&
						dayjs().isAfter(positionObj?.position?.expiresAt) ? (
						<Badge className="bg-red-700 text-white py-[3px]">Not accepting applications</Badge>
					) : null}
				</div>

				<div className="p-4 grow-2" onClick={handleViewRecommendations}>
					<RecruiterPositionCardTitle title={generatePositionTitle(positionObj.position.title as PositionTitle)} />

					<div className="mt-2">
						<RecruiterPositionCardDetail position={positionObj.position} />
					</div>

					<div className="mt-3">
						<RecruiterPositionCardRequiredSkills
							requiredSkills={positionObj.position.positionRequirement.skillThresholds ?? []}
						/>
					</div>
				</div>

				<RecruiterPositionCardFooter
					optionalData={optionalData}
					isPublished={positionObj.position.hasConfirmedPublish}
					positionId={String(positionObj.position._id)}
					positionStatus={positionObj.position.positionStatus}
					hideRecommendations={user?.flags?.hideRecommendations}
					recommendations={canShowRecommendedConnectedUserCount ? positionObj.recommendedUsers ?? 0 : '-'}
					connected={canShowRecommendedConnectedUserCount ? positionObj.connectedUsers ?? 0 : '-'}
					inPipeLineUsers={canShowRecommendedConnectedUserCount ? positionObj.inPipeLineUsers ?? 0 : '-'}
					handleViewRecommendations={handleViewRecommendations}
				/>

				<RecruiterPositionCardMoreOptions
					isBlacklisted={positionObj?.position?.company?.isBlacklisted}
					positionId={String(positionId) ?? ''}
					handleEditPosition={handleEditPosition}
					handleDuplicatePosition={handleDuplicatePosition}
					positionStatus={positionObj.position.positionStatus}
					handleClosePosition={handleClosePosition}
					handleViewPosition={handleViewPosition}
					handleReopenPosition={handleReopenPosition}
					handleAddCollaborator={handleAddCollaborator}
					handleUpdatePositionExpiry={handleUpdatePositionExpiry}
					handleUpdateApplicationExpiry={handleUpdateApplicationExpiry}
					handleUpdateAutoRecommendationExpiry={handleUpdateAutoRecommendationExpiry}
					customStyleClass="absolute top-4 right-4"
				/>
			</div>
		</>
	);
};

export default RecruiterPositionCard;
